import type React from 'react'
import { Calendar, Combine, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TodayIcon, TomorrowIcon } from '@/src/renderer/features/taskManagement/components/WhenDropdown'
import { DormantFilledIcon } from '@/src/renderer/components/icons/DormantIcon'
import { Dot } from '@/src/renderer/components/ui/Dot'
import type { ClarifyEntryCardProps } from './ClarifyEntryCard.types'
import type { StagedAction } from './useClarifyState'
import { formatRelativeAge, formatShortDate } from './clarifyFormatters'
import './ClarifyEntryCard.scss'

// Match the lucide icon API (size + optional className) so the shared meta
// icon renderer below can treat the filled Figma Today/Tomorrow icons
// interchangeably with the remaining lucide glyphs.
const TodayActionIcon: React.FC<{ size?: number; className?: string }> = ({
  size,
  className,
}) => (
  <span className={className}>
    <TodayIcon size={size} />
  </span>
)

const TomorrowActionIcon: React.FC<{ size?: number; className?: string }> = ({
  size,
  className,
}) => (
  <span className={className}>
    <TomorrowIcon size={size} />
  </span>
)

const ACTION_ICONS = {
  today: TodayActionIcon,
  tomorrow: TomorrowActionIcon,
  later: Calendar,
  pause: DormantFilledIcon,
  move: Combine,
  delete: Trash2,
} as const

/**
 * Card representation of one inbox entry in the Clarify-Mode left column.
 *
 * Four visual states:
 *  - `active`          — weiß mit violettem Border, zentraler Focus
 *  - `staged-inline`   — grauer BG, volle Höhe, Action-Icon inline statt start-date badge
 *  - `staged-in-stack` — kompakt (~2/3 Höhe), reduzierter Inhalt, für Stack-Overlay
 *  - `upcoming`        — weiß ohne Border, für noch nicht-bearbeitete Cards
 */
export function ClarifyEntryCard({
  task,
  variant,
  stagedAction,
  onClick,
  stackDepth = 0,
}: ClarifyEntryCardProps) {
  const { i18n } = useTranslation()
  const locale = i18n.language === 'en' ? 'en' : 'de'

  const meta = buildMeta(stagedAction, task.createdAt, locale)

  const classes = [
    'clarify-entry-card',
    `clarify-entry-card--${variant}`,
    stagedAction?.kind === 'delete' ? 'clarify-entry-card--destructive' : null,
    stagedAction?.kind === 'later' ? 'clarify-entry-card--later' : null,
    stagedAction?.kind === 'move' ? 'clarify-entry-card--move' : null,
    stagedAction ? 'clarify-entry-card--has-staged-action' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classes}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!onClick) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      data-stack-depth={variant === 'staged-in-stack' ? stackDepth : undefined}
    >
      <div className="clarify-entry-card__title">{task.title}</div>
      {variant !== 'staged-in-stack' && meta && (
        <div className="clarify-entry-card__meta">
          {meta.icon && (
            <>
              <meta.icon size={14} className="clarify-entry-card__meta-icon" />
              <Dot className="clarify-entry-card__meta-dot" />
            </>
          )}
          {meta.primary && <span className="clarify-entry-card__meta-primary">{meta.primary}</span>}
          {meta.primary && meta.secondary && <Dot className="clarify-entry-card__meta-dot" />}
          {meta.secondary && <span className="clarify-entry-card__meta-secondary">{meta.secondary}</span>}
        </div>
      )}
    </div>
  )
}

type MetaInfo = {
  icon: React.ComponentType<{ size?: number; className?: string }> | null
  primary: string | null
  secondary: string | null
}

function buildMeta(
  staged: StagedAction | undefined,
  createdAt: Date,
  locale: 'de' | 'en',
): MetaInfo | null {
  const age = formatRelativeAge(createdAt, locale)
  if (!staged) {
    return { icon: null, primary: null, secondary: age }
  }
  const Icon = ACTION_ICONS[staged.kind]
  if (staged.kind === 'later') {
    return { icon: Icon, primary: formatShortDate(staged.date, locale), secondary: age }
  }
  if (staged.kind === 'move') {
    return { icon: Icon, primary: staged.projectName, secondary: age }
  }
  return { icon: Icon, primary: null, secondary: age }
}
